import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { Field, FieldWrapper } from "@progress/kendo-react-form";
import { Label } from "@progress/kendo-react-labels";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { FormDropDownList, FormInput } from '../Utilities/FormComponents';
import AssetTemplateListItemDto from '../../../Models/Assets/AssetTemplateListItemDto';
import { enMessages } from '../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';
import ErrorCard from '../../Shared/Components/Cards/ErrorCard';
import LoadingSpinner from '../../Shared/Components/LoadingSpinner';
import { Groups, Endpoints, Gateways } from '../../../Models/Assets/AssetTemplateReadDto';
import { useAssetDetailsQueries } from '../Hooks/useAssetDetailsQueries';
import { needsTemplates } from '../Utilities/AssetDetailsUtils';
import TemplateSection from './TemplateSection';


interface AssetTemplate {
  id: string;
  name: string;
  imageId: string;
  hasGreasePumps: boolean;
  groups: Groups[];
  endpoints: Endpoints[];
  gateways: Gateways[];
}

interface AssetDetails {
  name: string;
  manufacturer: string;
  model: string;
  parentId: string;
  areaId: string;
  machineryType: string;
  description: string;
  photoUrl: string;
  cuid: string;
  namePlate: string;
  assetTemplate: AssetTemplate | null;
  greasePumpConnectivity: 'enabled' | 'disabled';
}

interface Props {
  assetData: any;
  isEdit: boolean;
  selectedTemplate: AssetTemplateListItemDto | null;
  onTemplateSelect: (template: AssetTemplateListItemDto | null) => void;
  onDetailsChange: (details: Partial<AssetDetails>) => void;
  parentAssets: Array<{ id: string; name: string; areaId: string }>;
  initialFormState?: Partial<AssetDetails>;
}

const MACHINERY_TYPE_OPTIONS = [
  { text: "Default", value: "Default" },
  { text: "HME", value: "HME" },
  { text: "Locomotive", value: "Locomotive" },
  { text: "Fixed Plant", value: "FixedPlant" },
] as const;

interface DropDownChangeEvent {
  value: { 
    id: string;
    name: string;
  } | null;
}

interface InputChangeEvent {
  value: string;
}

const AssetDetailsComponent: React.FC<Props> = ({ assetData = {}, isEdit, selectedTemplate, onTemplateSelect, onDetailsChange, parentAssets, initialFormState }) => {
  const localizationService = useLocalization();

  const [formState, setFormState] = useState<AssetDetails>(() => ({
    name: initialFormState?.name ?? '',
    manufacturer: initialFormState?.manufacturer ?? '',
    model: initialFormState?.model ?? '',
    parentId: initialFormState?.parentId ?? '',
    areaId: initialFormState?.areaId ?? '',
    machineryType: initialFormState?.machineryType ?? 'Default',
    description: initialFormState?.description ?? '',
    photoUrl: initialFormState?.photoUrl ?? '',
    cuid: initialFormState?.cuid ?? '',
    namePlate: initialFormState?.namePlate ?? '',
    assetTemplate: initialFormState?.assetTemplate ?? null,
    greasePumpConnectivity: initialFormState?.greasePumpConnectivity ?? 'disabled'
  }));

  // Track if we've initialized to prevent overwriting user changes
  const hasInitializedRef = useRef<string | null>(null);

  // Update formState when initialFormState changes
  useEffect(() => {
    if (initialFormState) {
      setFormState(prev => ({
        ...prev,
        ...initialFormState
      }));
    }
  }, [initialFormState]);

  const createFieldValidator = useCallback((fieldName: string) => (value: any): string | undefined => {
    // Required fields check
    // Only require areaId and template in Add mode
    const requiredFields = ['name'];
    if (!isEdit) {
      requiredFields.push('areaId');
      requiredFields.push('template');
    }
    if (requiredFields.includes(fieldName) && !value) {
      return localizationService.toLanguageString('custom.fieldIsRequired', enMessages.custom.fieldIsRequired);
    }


    // Length validation for text fields
    const textFields = ['name', 'manufacturer', 'model', 'description'];
    if (value && typeof value === 'string' && textFields.includes(fieldName)) {
      if (value.length < 2) {
        return localizationService.toLanguageString('custom.fieldMinLength', 'Field should be at least 2 characters long.');
      }

      // Name uniqueness check
      if (fieldName === 'name' && !isEdit) {
        const duplicate = parentAssets?.some(asset => 
          asset.name.toLowerCase() === value.toLowerCase() && 
          asset.areaId === formState.areaId
        );

        if (duplicate) {
          return localizationService.toLanguageString('custom.nameUnique', 'Name must be unique within the same location');
        }
      }
    }

    return undefined;
  }, [localizationService, isEdit, parentAssets, formState.areaId]);

  const nameValidator = useMemo(() => createFieldValidator('name'), [createFieldValidator]);
  const descriptionValidator = useMemo(() => createFieldValidator('description'), [createFieldValidator]);
  const manufacturerValidator = useMemo(() => createFieldValidator('manufacturer'), [createFieldValidator]);
  const modelValidator = useMemo(() => createFieldValidator('model'), [createFieldValidator]);

  const areaValidator = useMemo(() => createFieldValidator('areaId'), [createFieldValidator]);
  const templateValidator = useMemo(() => createFieldValidator('template'), [createFieldValidator]);

  const handleFieldChange = useCallback((fieldName: keyof AssetDetails, value: any) => {
    const updatedDetails = {
      ...formState,
      [fieldName]: value ?? '',
    };
    
    setFormState(updatedDetails);
    onDetailsChange(updatedDetails);
  }, [formState, onDetailsChange]);

  useEffect(() => {
    if (isEdit && assetData && assetData.id) {
      const currentAssetId = assetData.id;
      // Only initialize once per asset to prevent overwriting user changes
      if (hasInitializedRef.current !== currentAssetId) {
        hasInitializedRef.current = currentAssetId;
        
        // Build the new state
        const initialState: AssetDetails = {
          name: assetData.name ?? '',
          manufacturer: assetData.manufacturer ?? '',
          model: assetData.model ?? '',
          parentId: assetData.parentId ?? '',
          areaId: assetData.areaId ?? '',
          machineryType: assetData.machineryType ?? '',
          description: assetData.description ?? '',
          photoUrl: assetData.photoUrl ?? '',
          cuid: assetData.cuid ?? '',
          namePlate: assetData.namePlate ?? '',
          assetTemplate: assetData.assetTemplate ? {
            id: assetData.assetTemplate.id,
            name: assetData.assetTemplate.name,
            imageId: assetData.assetTemplate.imageId ?? '',
            hasGreasePumps: assetData.assetTemplate.hasGreasePumps ?? false,
            groups: assetData.assetTemplate.groups ?? [],
            endpoints: assetData.assetTemplate.endpoints ?? [],
            gateways: assetData.assetTemplate.gateways ?? []
          } : null,
          greasePumpConnectivity: assetData.assetTemplate?.isGreasePumpConnectivityEnabled ? 'enabled' : (assetData.greasePumpConnectivity ?? 'disabled')
        };

        setFormState(initialState);
        // Only notify parent on initial load, not on subsequent re-renders
        onDetailsChange(initialState);

        // If we have an asset template, notify parent
        if (assetData.assetTemplate) {
          onTemplateSelect(assetData.assetTemplate);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, assetData?.id, onTemplateSelect, onDetailsChange]);

  // Usar hook customizado para queries
  const { locationsQuery, templatesQuery, assetImageQuery } = useAssetDetailsQueries(
    isEdit,
    formState.machineryType,
    assetData,
    selectedTemplate
  );

  const templates = useMemo(() => templatesQuery.data || [], [templatesQuery.data]);

  const handleNameChange = useCallback((e: InputChangeEvent) => handleFieldChange('name', e.value), [handleFieldChange]);
  const handleDescriptionChange = useCallback((e: any) => handleFieldChange('description', e.value), [handleFieldChange]);
  const handleManufacturerChange = useCallback((e: any) => handleFieldChange('manufacturer', e.value), [handleFieldChange]);
  const handleModelChange = useCallback((e: any) => handleFieldChange('model', e.value), [handleFieldChange]);
  const handleLocationChange = useCallback((e: DropDownChangeEvent) => {
    const updatedDetails = {
      ...formState,
      areaId: e.value?.id ?? '',
      parentId: ''
    };
    setFormState(updatedDetails);
    onDetailsChange(updatedDetails);
  }, [formState, onDetailsChange]);
  const handleMachineryTypeChange = useCallback((e: any) => {
    const val = e.value;
    const machineryType = typeof val === 'object' && val?.value != null ? val.value : val;
    handleFieldChange('machineryType', machineryType);
  }, [handleFieldChange]);

  const handleParentChange = useCallback((e: any) => {
    const selectedParent = e.value;
    handleFieldChange('parentId', selectedParent?.id || '');
  }, [handleFieldChange]);

  const handleGreasePumpConnectivityChange = useCallback((connectivity: 'enabled' | 'disabled', event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
    }
    handleFieldChange('greasePumpConnectivity', connectivity);
  }, [handleFieldChange]);

  const handleTemplateChange = useCallback((event: DropDownChangeEvent) => {
    const selectedId = event.value?.id ?? event.value;
    const selected = templates.find(t => t.id === selectedId);
    
    if (selected) {
      const templateForState: AssetTemplate = {
        id: selected.id,
        name: selected.name,
        imageId: '', // full template (with imageId) is loaded separately when needed
        hasGreasePumps: selected.hasGreasePumps,
        groups: [],
        endpoints: [],
        gateways: []
      };
      
      // Update both local state and parent state
      const updatedDetails = {
        ...formState,
        assetTemplate: templateForState,
        greasePumpConnectivity: selected.hasGreasePumps ? formState.greasePumpConnectivity : 'disabled'
      };
      
      setFormState(updatedDetails);
      onDetailsChange(updatedDetails);
      onTemplateSelect(selected);
    } else {
      const updatedDetails = {
        ...formState,
        assetTemplate: null
      };
      
      setFormState(updatedDetails);
      onDetailsChange(updatedDetails);
      onTemplateSelect(null);
    }
  }, [templates, onTemplateSelect, formState, onDetailsChange]);

  const filteredParentAssets = useMemo(() => {
    if (!formState.areaId) {
      return [];
    }
    return parentAssets.filter((parent: any) => parent.areaId === formState.areaId);
  }, [parentAssets, formState.areaId]);

  if(locationsQuery.isLoading || locationsQuery.isFetching) {
    return <LoadingSpinner />;
  }

  if(locationsQuery.error) {
    return <ErrorCard />;
  }

  // Show loading only for templates if we're fetching and don't have previous data
  if(templatesQuery.isLoading && !templatesQuery.data) {
    return <LoadingSpinner />;
  }

  return (
    <div className="flex flex-col gap-1 [&_.k-form-field]:!mb-0">
      <Field
        key="name"
        id="name"
        name="name"
        label={localizationService.toLanguageString('custom.name', enMessages.custom.name) + " *"}
        component={FormInput}
        value={formState.name}
        onChange={handleNameChange}
        validator={nameValidator}
      />
      <Field
        key="notes"
        id="notes"
        name="notes"
        label={localizationService.toLanguageString('custom.description', enMessages.custom.description)}
        component={FormInput}
        value={formState.description}
        onChange={handleDescriptionChange}
        validator={descriptionValidator}
      />
      <div className="flex gap-4">
        <div className="flex-1">
          <Field
            key="manufacturer"
            id="manufacturer"
            name="manufacturer"
            label={localizationService.toLanguageString('custom.manufacturer', enMessages.custom.manufacturer)}
            component={FormInput}
            value={formState.manufacturer}
            onChange={handleManufacturerChange}
            validator={manufacturerValidator}
          />
        </div>
        <div className="flex-1">
          <Field
            key="model"
            id="model"
            name="model"
            label={localizationService.toLanguageString('custom.model', enMessages.custom.model)}
            component={FormInput}
            value={formState.model}
            onChange={handleModelChange}
            validator={modelValidator}
          />
        </div>
      </div>
      <div className="flex gap-4">
        <div className="flex-1">
          <Field
            key="location"
            id="location"
            name="location"
            label={localizationService.toLanguageString('custom.location', enMessages.custom.location) + " *"}
            component={FormDropDownList}
            data={locationsQuery.data?.slice().sort((a, b) => a.name.localeCompare(b.name))}
            textField="name"
            valueField="id"
            defaultValue={formState.areaId ? locationsQuery.data?.find(loc => loc.id === formState.areaId) : undefined}
            value={formState.areaId ? locationsQuery.data?.find(loc => loc.id === formState.areaId) : undefined}
            onChange={handleLocationChange}
            validator={areaValidator}
            clearButton={true}
          />
        </div>
        <div className="flex-1">
          <Field
            key="parentName"
            id="parentName"
            name="parentName"
            label={localizationService.toLanguageString('custom.parentAsset', enMessages.custom.parentAsset)}
            component={FormDropDownList}
            data={filteredParentAssets.slice().sort((a, b) => a.name.localeCompare(b.name))}
            textField="name"
            valueField="id"
            value={formState.parentId ? filteredParentAssets.find(p => p.id === formState.parentId) : undefined}
            onChange={handleParentChange}
            disabled={!formState.areaId}
            clearButton={true}
          />
        </div>
      </div>
      <FieldWrapper>
        <Label id="machineryType_label" editorId="machineryType" editorDisabled={isEdit}>
          {localizationService.toLanguageString('custom.type', enMessages.custom.type)}
        </Label>
        <DropDownList
          id="machineryType"
          data={[...MACHINERY_TYPE_OPTIONS]}
          textField="text"
          dataItemKey="value"
          value={MACHINERY_TYPE_OPTIONS.find((d) => d.value === ((isEdit ? assetData?.machineryType : formState.machineryType) || "Default")) ?? MACHINERY_TYPE_OPTIONS[0]}
          onChange={handleMachineryTypeChange}
          disabled={isEdit}
          filterable={false}
          style={{ width: "100%" }}
        />
      </FieldWrapper>
      {needsTemplates(formState.machineryType) && (
        <TemplateSection
          templates={templates}
          assetData={assetData}
          isEdit={isEdit}
          selectedTemplate={selectedTemplate}
          formState={formState}
          assetImageQuery={assetImageQuery}
          onTemplateChange={handleTemplateChange}
          onGreasePumpConnectivityChange={handleGreasePumpConnectivityChange}
          templateValidator={templateValidator}
        />
      )}
    </div>
  );
};

export default AssetDetailsComponent; 